package top.wcpe.allincore.dsl.compiler.diag

import top.wcpe.allincore.dsl.compiler.source.SourcePosition
import top.wcpe.allincore.dsl.compiler.source.SourceRange

/**
 * 诊断严重级别。
 *
 * - [ERROR]：编译失败级别，IDE 端红波浪线
 * - [WARNING]：黄波浪线，不阻塞编译
 * - [INFO]：M9 Stage 6 引入，IDE 端映射为 WEAK_WARNING（灰色弱警告），仅在 inspection
 *   列表与 IDE settings 内可见，避免对历史样本造成误报洪水
 */
enum class DiagnosticSeverity { ERROR, WARNING, INFO }

/**
 * 单条编译诊断。
 *
 * @property code     错误码（如 "DSL_E001"）
 * @property severity 严重级别
 * @property message  人类可读消息（中文）
 * @property position 源位置
 */
data class Diagnostic(
    val code: String,
    val severity: DiagnosticSeverity,
    val message: String,
    val position: SourcePosition,
    /** M5-f：可选的源码片段，用于错误信息展示。 */
    val sourceSnippet: SourceSnippet? = null,
    /** M5-f：可选的修复建议。 */
    val fixHint: String? = null,
    /** UI DSL v2：legacy 迁移目标与可信度。 */
    val migration: MigrationHint? = null,
) {
    /** 规范化诊断使用的源码范围。 */
    val range: SourceRange
        get() = position.range

    /**
     * 标准格式化输出（保持向后兼容，单行紧凑形式）。
     *
     * 不含源码片段与修复建议；如需富信息请使用 [formatRich]。
     */
    fun format(): String = "[${position.line}:${position.column}] $severity $code: $message"

    /** [format] 的显式别名，语义更清晰，便于与 [formatRich] 对照使用。 */
    fun formatSimple(): String = format()

    /**
     * M5-f：富格式输出，包含源码片段与修复建议。
     *
     * - 当 [sourceSnippet] 为 null 时降级为 [formatSimple]，保持现有行为
     * - 否则按 RFC §8.2 排版：标题行 / 位置行 / 上下文片段 / 修复建议（可选）
     *
     * 排版示例：
     * ```
     * DSL_E607: suspend lambda 'fetcher' 必须用 await 调用
     *   at hello.allin:5:12
     *        4|   var fetcher = { id -> await fetch(id) }
     *        5|   var name = fetcher(123)
     *                        ^^^^^^^^^^^^
     *   修复建议：var name = await fetcher(123)
     * ```
     */
    fun formatRich(): String {
        val snippet = sourceSnippet ?: return formatSimple()

        val sb = StringBuilder()
        // 标题行：错误码 + 消息
        sb.append(code).append(": ").append(message).append('\n')
        // 位置行：文件路径 + 行 + 列
        sb.append("  at ")
            .append(snippet.filePath).append(':')
            .append(snippet.lineNumber).append(':')
            .append(snippet.columnNumber)
            .append('\n')

        // 上下文片段：行号右对齐 + "| " + 行原文；错误行下一行追加下划线标记
        // 行号宽度按片段最大行号决定，确保多位行号也能对齐
        val firstLineNumber = snippet.lineNumber - snippet.errorLineIndex
        val lastLineNumber = firstLineNumber + snippet.contextLines.size - 1
        val gutterWidth = lastLineNumber.toString().length
        val gutterIndent = " ".repeat(gutterWidth)

        snippet.contextLines.forEachIndexed { idx, lineText ->
            val lineNo = firstLineNumber + idx
            sb.append("    ")
                .append(lineNo.toString().padStart(gutterWidth))
                .append("| ")
                .append(lineText)
                .append('\n')
            if (idx == snippet.errorLineIndex) {
                // 下划线标记：左侧补与行号 + "| " 等宽的空白，再按 markerStart 缩进
                val markerLength = snippet.markerLength.coerceAtLeast(1)
                sb.append("    ")
                    .append(gutterIndent)
                    .append("  ")
                    .append(" ".repeat(snippet.markerStart))
                    .append("^".repeat(markerLength))
                    .append('\n')
            }
        }

        // 修复建议（可选）
        fixHint?.let { sb.append("  修复建议：").append(it).append('\n') }

        // 末尾保留单个换行符即可，去掉最后的多余空行
        return sb.toString().trimEnd('\n')
    }
}

enum class MigrationConfidence(val wireName: String) {
    SAFE("safe"),
    SUGGESTED("suggested"),
    MANUAL("manual"),
}

data class MigrationHint(
    val target: String,
    val confidence: MigrationConfidence,
)
